"use client"

import type React from "react"
import { useState } from "react"
import { Bell } from "lucide-react"
import type { Goal } from "@/app/goals/page"
import { NotificationsModal } from "@/components/ui/notifications/notifications-modal"

interface GoalsHeaderProps {
  onAddGoal: (goal: Omit<Goal, "id">) => void
}

export default function GoalsHeader({ onAddGoal }: GoalsHeaderProps) {
  const [showModal, setShowModal] = useState(false)
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false)
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    progress: 0,
    dueDate: "",
    icon: "flag",
    iconBgColor: "bg-indigo-100",
    iconColor: "text-indigo-500",
    isCompleted: false,
  })

  // Mock function to handle marking notifications as read
  const handleMarkAsRead = (notificationId: string) => {
    console.log("Marking notification as read:", notificationId)
    // Implement your logic to mark notification as read
  }

  const handleMarkAllAsRead = () => {
    console.log("Marking all notifications as read")
    // Implement your logic to mark all notifications as read
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (formData.title.trim()) {
      onAddGoal({ ...formData, icon: formData.icon as Goal["icon"] })

      setFormData({
        title: "",
        description: "",
        progress: 0,
        dueDate: "",
        icon: "flag",
        iconBgColor: "bg-indigo-100",
        iconColor: "text-indigo-500",
        isCompleted: false,
      })
      setShowModal(false)
    }
  }

  return (
    <>
      <header className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">My Goals</h1>
          <p className="mt-1 text-slate-500">Track your progress and reach for the stars!</p>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Notifications Bell Button */}
          <button 
            onClick={() => setIsNotificationsOpen(true)}
            className="relative p-2 rounded-full hover:bg-slate-100 text-slate-600 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            aria-label="View notifications"
          >
            <Bell className="w-6 h-6 hover:text-indigo-600 transition-colors" />
            <span className="absolute top-1 right-1 block h-2.5 w-2.5 rounded-full bg-red-500 border-2 border-white animate-pulse"></span>
          </button>
          
          {/* Add Goal Button */}
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 rounded-lg bg-indigo-600 px-6 py-3 font-semibold text-white shadow-lg shadow-indigo-600/30 transition-all hover:bg-indigo-700 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          >
            <span>Add New Goal</span>
          </button>
        </div>
      </header>

      {/* Notifications Modal */}
      <NotificationsModal
        isOpen={isNotificationsOpen}
        onClose={() => setIsNotificationsOpen(false)}
        onMarkAsRead={handleMarkAsRead}
        onMarkAllAsRead={handleMarkAllAsRead}
        position="top-right"
      />

      {/* Add Goal Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h2 className="mb-4 text-2xl font-bold text-slate-900">Add New Goal</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700">Goal Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 placeholder-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                  placeholder="Enter goal title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 placeholder-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                  placeholder="Enter goal description"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Due Date</label>
                <input
                  type="text"
                  value={formData.dueDate}
                  onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 placeholder-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                  placeholder="e.g., Dec 15, 2024"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 rounded-lg border border-slate-300 px-4 py-2 font-medium text-slate-700 transition-colors hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 rounded-lg bg-indigo-600 px-4 py-2 font-medium text-white transition-colors hover:bg-indigo-700"
                >
                  Add Goal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}